import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';

import type { BinderTagRequest } from 'src/api-sdk';
import { useAPI } from 'src/util/use-api';
import type {
  BinderFilters,
  BindersFilters,
} from 'src/util/context/BinderContext';
import { QUERIES } from 'src/util/globals';

type UseBindersParams = {
  enabled?: boolean;
  searchQuery?: string;
  annotateArticleId?: string;
  annotateComplianceEntityResultId?: string;
};

type ResultHooks = {
  onSuccess?: () => void;
  onError?: () => void;
};

export const useBindersList = (
  filters: BindersFilters = {},
  {
    enabled = true,
    annotateArticleId,
    annotateComplianceEntityResultId,
  }: UseBindersParams = {}
) => {
  const { API } = useAPI();

  return useInfiniteQuery({
    queryKey: [
      QUERIES.BINDERS,
      { filters },
      annotateArticleId,
      annotateComplianceEntityResultId,
    ],

    queryFn: async ({ pageParam }) => {
      return (
        await API?.bindersAPI.bindersList({
          query: filters.searchQuery ?? '',
          nextKey: pageParam,
          sorting: filters.sorting ?? 'alphabetical',
          sortingOrder: filters.sortingOrder ?? 'ascending',
          ownedBinders: filters.ownedBinders ?? false,
          annotateArticleId,
          annotateComplianceEntityResultId,
        })
      )?.data;
    },

    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage?.next_key ?? undefined,
    enabled,
    placeholderData: keepPreviousData,
  });
};

type AddOrRemoveToBinderParams = {
  binderId: string;
  articleId: string | null;
  complianceEntityResultId: string | null;
  tags?: BinderTagRequest[];
  note?: string;
};

type RemoveFromBinderParams = {
  binderId: string;
  binderItemId: string;
};

export const useAddToBinder = ({ onError, onSuccess }: ResultHooks = {}) => {
  const { API } = useAPI();

  return useMutation({
    mutationFn: async ({
      articleId,
      complianceEntityResultId,
      binderId,
      tags = [],
      note,
    }: AddOrRemoveToBinderParams) => {
      const binderItemData = (
        await API?.bindersAPI.bindersItemsCreate({
          binderId,
          binderItemRequest: {
            tags,
            article: articleId ? { external_id: articleId } : null,
            // @ts-ignore
            compliance_entity_result: complianceEntityResultId
              ? { external_id: complianceEntityResultId }
              : null,
            comment: note,
          },
        })
      )?.data;
      return binderItemData;
    },

    onSuccess,
    onError,
  });
};

export const useRemoveFromBinder = ({ onError, onSuccess }: ResultHooks) => {
  const { API } = useAPI();

  return useMutation({
    mutationFn: async ({ binderItemId, binderId }: RemoveFromBinderParams) => {
      return (
        await API?.bindersAPI.bindersItemsDestroy({
          binderId,
          binderItemId,
        })
      )?.data;
    },

    onSuccess,
    onError,
  });
};

type UseBinderDataParams = {
  binderId: string;
};

export const useBinderData = ({ binderId }: UseBinderDataParams) => {
  const { API } = useAPI();

  return useQuery({
    queryKey: [QUERIES.BINDER_DATA, binderId],

    queryFn: async () => {
      return (
        await API?.bindersAPI.bindersRetrieve({
          binderId,
        })
      )?.data;
    },
  });
};

type UseBinderItemsParams = {
  binderId: string;
  searchQuery?: string;
  enabled?: boolean;
  sorting: BinderFilters['sorting'];
  tags?: string[];
};

export const useBinderItems = ({
  binderId,
  enabled = true,
  searchQuery,
  sorting,
  tags = [],
}: UseBinderItemsParams) => {
  const { API } = useAPI();

  return useInfiniteQuery({
    queryKey: [QUERIES.BINDER_DATA, binderId, searchQuery, sorting, tags],

    queryFn: async ({ pageParam }) => {
      return (
        await API?.bindersAPI.bindersItemsList({
          binderId,
          sorting,
          query: searchQuery,
          nextKey: pageParam,
          tags,
        })
      )?.data;
    },

    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage?.next_key ?? undefined,
    enabled,
    placeholderData: keepPreviousData,
  });
};

export const useRemoveBinder = ({ onError, onSuccess }: ResultHooks = {}) => {
  const { API } = useAPI();

  return useMutation({
    mutationFn: async (binderId: string) => {
      return (
        await API?.bindersAPI.bindersDestroy({
          binderId,
        })
      )?.data;
    },

    onSuccess,
    onError,
  });
};

type UpdateBinderParams = {
  binderId: string;
  name: string;
};

export const useUpdateBinder = ({ onError, onSuccess }: ResultHooks = {}) => {
  const { API } = useAPI();

  return useMutation({
    mutationFn: async ({ binderId, name }: UpdateBinderParams) => {
      return (
        await API?.bindersAPI.bindersUpdate({
          binderId,
          binderRequest: {
            name,
          },
        })
      )?.data;
    },

    onSuccess,
    onError,
  });
};

type UseBindersTagsParams = {
  binderId: string;
  query?: string;
  enabled?: boolean;
};

export const useBinderTags = ({
  binderId,
  enabled = true,
  query,
}: UseBindersTagsParams) => {
  const { API } = useAPI();

  return useInfiniteQuery({
    queryKey: [QUERIES.BINDER_TAGS, binderId, query],

    queryFn: async ({ pageParam }) => {
      return (
        await API?.bindersAPI.bindersTagsList({
          sorting: 'created_at',
          binderId,
          sortingOrder: 'descending',
          nextKey: pageParam,
          query,
        })
      )?.data;
    },

    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage?.next_key ?? undefined,
    gcTime: 0,
    staleTime: 0,
    enabled,
    placeholderData: keepPreviousData,
  });
};

type UseBinderItemParams = {
  binderId: string;
  binderItemId: string;
};

export const useBinderItem = (
  { binderId, binderItemId }: UseBinderItemParams,
  { enabled = false }: { enabled?: boolean } = {}
) => {
  const { API } = useAPI();

  return useQuery({
    queryKey: [QUERIES.BINDER_ITEM, binderId, binderItemId],

    queryFn: async () => {
      return (
        await API?.bindersAPI.bindersItemsRetrieve({
          binderId,
          binderItemId,
        })
      )?.data;
    },

    enabled,
  });
};

type UseBinderItemNotesParams = {
  binderId: string;
  binderItemId: string;
  enabled?: boolean;
};

export const useBinderItemNotes = ({
  binderId,
  binderItemId,
  enabled = true,
}: UseBinderItemNotesParams) => {
  const { API } = useAPI();

  return useInfiniteQuery({
    queryKey: [QUERIES.BINDER_ITEM_NOTES, binderId, binderItemId],

    queryFn: async ({ pageParam }) => {
      return (
        await API?.activitiesAPI.activitiesCommentsList({
          binderItemId,
          nextKey: pageParam,
          sorting: 'created_at',
          sortingOrder: 'descending',
        })
      )?.data;
    },

    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage?.next_key ?? undefined,
    enabled,
    placeholderData: keepPreviousData,
  });
};

type UsePostBinderNoteParams = {
  binderId: string;
  binderItemId: string;
};

export const usePostBinderNote = (
  { binderItemId }: UsePostBinderNoteParams,
  { onError, onSuccess }: ResultHooks = {}
) => {
  const { API } = useAPI();

  return useMutation({
    mutationFn: async (note: string) => {
      return (
        await API?.activitiesAPI.activitiesCommentsCreate({
          commentRequest: {
            binder_item: binderItemId,
            text: note,
          },
        })
      )?.data;
    },

    onSuccess,
    onError,
  });
};

export const useBinderItemTagsUpdate = (
  { binderId, binderItemId }: UsePostBinderNoteParams,
  { onError, onSuccess }: ResultHooks = {}
) => {
  const { API } = useAPI();

  return useMutation({
    mutationFn: async (tags: string[]) => {
      return await API?.bindersAPI.bindersItemsPartialUpdate({
        binderId,
        binderItemId,
        patchedBinderItemRequest: {
          tags: tags.map((t) => ({ text: t })),
        },
      });
    },

    onSuccess,
    onError,
  });
};

type UseBinderNotesParams = {
  binderId: string;
  binderItemId?: string;
};

type UseBinderNotesOptions = {
  enabled?: boolean;
} & ResultHooks;

export const useBinderNotes = (
  { binderId }: UseBinderNotesParams,
  { enabled = true }: UseBinderNotesOptions = {}
) => {
  const { API } = useAPI();

  return useInfiniteQuery({
    queryKey: [QUERIES.BINDER_NOTES, binderId],

    queryFn: async ({ pageParam }) => {
      return (
        await API?.activitiesAPI.activitiesCommentsList({
          binderId: binderId,
          nextKey: pageParam ?? undefined,
        })
      )?.data;
    },

    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage?.next_key ?? undefined,
    enabled,
    placeholderData: keepPreviousData,
  });
};
